import { combineReducers } from '@reduxjs/toolkit';
import { captureException } from 'fe-core/util/errorTracking';

import { reducer as claimRequestsReducer } from './claimRequests/slice';
import { reducer as claimRequestTotalsReducer } from './claimRequestTotals/slice';
import { reducer as commonShiftsReducer } from './commonShifts/slice';
import { reducer as companyHoursReducer } from './companyHours/slice';
import { reducer as conflictsReducer } from './conflicts/slice';
import { reducer as customJobPositionsSliceReducer } from './customJobPositions/slice';
import { reducer as departmentsSliceReducer } from './departments/slice';
import { reducer as estimatedSalesReducer } from './estimatedSales/slice';
import { reducer as eventQuotasReducer } from './eventQuotas/slice';
import { reducer as eventsSliceReducer } from './events/slice';
import { reducer as jobPositionsSliceReducer } from './jobPositions/slice';
import { reducer as jobsSliceReducer } from './jobs/slice';
import { reducer as laborTargetsReducer } from './laborTargets/slice';
import { reducer as locationsSliceReducer } from './locations/slice';
import { reducer as propertiesReducer } from './properties/slice';
import { reducer as publishHistoryEventsReducer } from './publishHistoryEvents/slice';
import { reducer as reassignmentSliceReducer } from './reassignmentRecommendations/slice';
import { reducer as roleLaborTotalsReducer } from './roleLaborTotals/slice';
import { reducer as rolesSliceReducer } from './roles/slice';
import { reducer as roleWagesReducer } from './roleWages/slice';
import { reducer as scheduleSliceReducer } from './schedule/slice';
import { reducer as scheduleAvailabilitiesSliceReducer } from './scheduleAvailabilities/slice';
import { reducer as schedulingInsightsEventsReducer } from './schedulingInsightsEvents/slice';
import { reducer as sessionReducer } from './session/slice';
import { reducer as shiftEventsReducer } from './shiftEvents/slice';
import { reducer as shiftLaborTotalsReducer } from './shiftLaborTotals/slice';
import { reducer as shiftNotesReducer } from './shiftNotes/slice';
import { reducer as shiftNotesQuotasReducer } from './shiftNotesQuotas/slice';
import { reducer as shiftsSliceReducer } from './shifts/slice';
import { reducer as templatesSliceReducer } from './templates/slice';
import { reducer as timeOffBlackoutPeriodsSliceReducer } from './timeOffBlackoutPeriods/slice';
import { reducer as timeOffsSliceReducer } from './timeOffs/slice';
import { reducer as usersSliceReducer } from './users/slice';
import { reducer as weatherForecastReducer } from './weatherForecast/slice';

const createRootReducer = () => {
  try {
    return combineReducers({
      claimRequests: claimRequestsReducer,
      claimRequestTotals: claimRequestTotalsReducer,
      commonShifts: commonShiftsReducer,
      companyHours: companyHoursReducer,
      conflicts: conflictsReducer,
      customJobPositions: customJobPositionsSliceReducer,
      departments: departmentsSliceReducer,
      estimatedSales: estimatedSalesReducer,
      eventQuotas: eventQuotasReducer,
      events: eventsSliceReducer,
      jobPositions: jobPositionsSliceReducer,
      jobs: jobsSliceReducer,
      laborTargets: laborTargetsReducer,
      locations: locationsSliceReducer,
      schedule: scheduleSliceReducer,
      properties: propertiesReducer,
      publishHistoryEvents: publishHistoryEventsReducer,
      reassignment: reassignmentSliceReducer,
      roleLaborTotals: roleLaborTotalsReducer,
      roleWages: roleWagesReducer,
      roles: rolesSliceReducer,
      scheduleAvailabilities: scheduleAvailabilitiesSliceReducer,
      schedulingInsightsEvents: schedulingInsightsEventsReducer,
      session: sessionReducer,
      shiftEvents: shiftEventsReducer,
      shiftLaborTotals: shiftLaborTotalsReducer,
      shiftNotes: shiftNotesReducer,
      shiftNotesQuotas: shiftNotesQuotasReducer,
      shifts: shiftsSliceReducer,
      templates: templatesSliceReducer,
      timeOffBlackoutPeriods: timeOffBlackoutPeriodsSliceReducer,
      timeOffs: timeOffsSliceReducer,
      users: usersSliceReducer,
      weatherForecast: weatherForecastReducer,
    });
  } catch (error) {
    captureException(error);
    return () => {};
  }
};

export const scheduleBuilderReducer = createRootReducer();

export type RootState = ReturnType<typeof scheduleBuilderReducer>;

export * as claimRequestsReducer from './claimRequests/slice';
export * as claimRequestTotalsReducer from './claimRequestTotals/slice';
export * as commonShiftsReducer from './commonShifts/slice';
export * as companyHoursReducer from './companyHours/slice';
export * as conflictsReducer from './conflicts/slice';
export * as customJobPositionsSliceReducer from './customJobPositions/slice';
export * as departmentsSliceReducer from './departments/slice';
export * as estimatedSalesReducer from './estimatedSales/slice';
export * as eventQuotasReducer from './eventQuotas/slice';
export * as eventsSliceReducer from './events/slice';
export * as jobPositionsSliceReducer from './jobPositions/slice';
export * as jobsSliceReducer from './jobs/slice';
export * as laborTargetsReducer from './laborTargets/slice';
export * as locationsSliceReducer from './locations/slice';
export * as publishHistoryEventsReducer from './publishHistoryEvents/slice';
export * as reassignmentReducer from './reassignmentRecommendations/slice';
export * as roleLaborTotalsReducer from './roleLaborTotals/slice';
export * as roleWagesReducer from './roleWages/slice';
export * as rolesSliceReducer from './roles/slice';
export * as scheduleAvailabilitiesSliceReducer from './scheduleAvailabilities/slice';
export * as schedulingInsightsEventsReducer from './schedulingInsightsEvents/slice';
export * as scheduleSliceReducer from './schedule/slice';
export * as sessionReducer from './session/slice';
export * as shiftEventsReducer from './shiftEvents/slice';
export * as shiftLaborTotalsReducer from './shiftLaborTotals/slice';
export * as shiftNotesQuotasReducer from './shiftNotesQuotas/slice';
export * as shiftNotesReducer from './shiftNotes/slice';
export * as shiftsSliceReducer from './shifts/slice';
export * as templatesSliceReducer from './templates/slice';
export * as timeOffBlackoutPeriodsSliceReducer from './timeOffBlackoutPeriods/slice';
export * as timeOffsSliceReducer from './timeOffs/slice';
export * as usersSliceReducer from './users/slice';
